/**
 * Which languages get a visible tab, per browser.
 *
 * The bar shows three languages plus a dropdown for the rest, so the three on
 * show should be the three *this* visitor is most likely to want — not a fixed
 * list picked by us. The seed comes from `navigator.languages`, which is what
 * the person actually configured their browser to read, and from then on the
 * list is most-recently-used: picking a language out of the dropdown promotes
 * it onto the bar, exactly like the tab bar of any editor.
 *
 * Source and target keep separate lists. Someone whose browser says Korean
 * mostly *reads* English and *writes* Korean, so seeding both sides from one
 * list would put the wrong language on one of them.
 */

export const TAB_COUNT = 3;

const KEY_SRC = 'gt_tabs_src';
const KEY_TGT = 'gt_tabs_tgt';

/**
 * Fallback ordering when the browser tells us nothing useful. English first
 * because it is the product's centre of gravity; the rest by rough speaker
 * count among the five supported.
 */
const FALLBACK = ['en', 'zh', 'es', 'ja', 'ko'];

/** Map a BCP-47 tag onto one of our codes. `zh-Hant`, `zh-TW`, `cmn` → `zh`. */
function normalize(tag: string, supported: string[]): string | null {
  const lower = tag.toLowerCase();
  const primary = lower.split('-')[0];
  if (supported.includes(primary)) return primary;
  // A couple of tags whose primary subtag is not the code we use.
  if (primary === 'cmn' || primary === 'yue' || primary === 'zho') {
    return supported.includes('zh') ? 'zh' : null;
  }
  return null;
}

/** The visitor's own languages, in their stated order of preference. */
export function browserLanguages(supported: string[]): string[] {
  if (typeof navigator === 'undefined') return [];
  const tags = navigator.languages?.length
    ? [...navigator.languages]
    : [navigator.language].filter(Boolean);

  const out: string[] = [];
  for (const tag of tags) {
    const code = normalize(String(tag), supported);
    if (code && !out.includes(code)) out.push(code);
  }
  return out;
}

function dedupe(list: string[], supported: string[]): string[] {
  const out: string[] = [];
  for (const c of list) {
    if (supported.includes(c) && !out.includes(c)) out.push(c);
  }
  return out;
}

function read(key: string): string[] | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : null;
  } catch {
    return null;
  }
}

/**
 * The ordered language list for one side of the bar. Stored order wins (the
 * visitor built it by using the app); otherwise it is seeded from the browser.
 */
export function loadTabs(side: 'source' | 'target', supported: string[]): string[] {
  const key = side === 'source' ? KEY_SRC : KEY_TGT;
  const stored = typeof window !== 'undefined' ? read(key) : null;
  if (stored?.length) return dedupe([...stored, ...FALLBACK], supported);

  const mine = browserLanguages(supported);
  const seed =
    side === 'target'
      // Target: English leads. The whole point of the product is understanding
      // English, and a visitor who wants their own language is one tap away.
      ? ['en', ...mine, ...FALLBACK]
      // Source: whatever they read and write, then English.
      : [...mine, 'en', ...FALLBACK];

  return dedupe(seed, supported);
}

export function saveTabs(side: 'source' | 'target', order: string[]): void {
  try {
    localStorage.setItem(side === 'source' ? KEY_SRC : KEY_TGT, JSON.stringify(order.slice(0, 8)));
  } catch {
    /* private mode — the bar still works, it just won't be remembered */
  }
}

/**
 * Move `code` to the front so it occupies a visible tab. Used when a language
 * is chosen from the dropdown, and after a swap, so the bar reflects what the
 * visitor actually uses instead of what they used once on their first visit.
 */
export function promote(order: string[], code: string, supported: string[]): string[] {
  return dedupe([code, ...order, ...FALLBACK], supported);
}

// ── The active selection ─────────────────────────────────────────────────────
// The tab lists above remember which languages are *on the bar*. These remember
// which one is actually *chosen*, plus the mode — without them a returning
// visitor finds their bar intact but the selection reset, which reads as the
// app having forgotten them.
//
// The SOURCE is deliberately not persisted. "Detect language" is the intended
// default every time: it is the setting that works for any text pasted in, and
// restoring a concrete source would silently mistranslate the first thing a
// returning visitor pastes in a different language.

const KEY_TARGET = 'gt_last_target';
const KEY_MODE = 'gt_last_mode';

function readString(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null; // private mode — the app still works, it just won't remember
  }
}

function writeString(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode */
  }
}

/** Last target language, if it is still one we support. */
export function loadLastTarget(supported: string[]): string | null {
  const v = readString(KEY_TARGET);
  return v && supported.includes(v) ? v : null;
}

export function saveLastTarget(code: string): void {
  writeString(KEY_TARGET, code);
}

/**
 * Last mode, if it is still available.
 *
 * Validated against the CURRENT list because the admin dashboard can switch a
 * mode off. A stored id that no longer exists would otherwise leave the app in
 * a mode with no button, whose requests the server refuses.
 */
export function loadLastMode(available: string[]): string | null {
  const v = readString(KEY_MODE);
  return v && available.includes(v) ? v : null;
}

export function saveLastMode(id: string): void {
  writeString(KEY_MODE, id);
}

// ── The overview toggle ─────────────────────────────────────────────────────

const KEY_OVERVIEW = 'gt_overview';
const KEY_OVERVIEW_SEEN = 'gt_overview_seen';

/**
 * Whether to SHOW the one-sentence summary.
 *
 * Off until the visitor asks for it, and remembered from then on.
 *
 * It spent a while defaulting ON, on the argument that the summary now rides
 * along with every answer either way — the switch decides the DRAWING, not the
 * REQUEST (see the note in `run` in Translator), so showing it costs nothing
 * extra and a lightning-bolt button explains nothing about itself to somebody
 * who has never seen it lit.
 *
 * That argument was about teaching, and the teaching is done: the switch has
 * been on by default long enough that the people using this know the card is
 * there and know which button makes it come back. What is left of the default
 * is a card nobody asked for above every translation, so the default goes back
 * to off and the sparkle (see `loadOverviewSeen`) does the introducing — which
 * is what it was built for, and which only runs for someone who has genuinely
 * never pressed the button.
 *
 * `=== '1'` rather than `!== '0'`: absent means "has never chosen", which is
 * off, and only an explicit on is on.
 */
export function loadOverview(): boolean {
  return readString(KEY_OVERVIEW) === '1';
}

export function saveOverview(on: boolean): void {
  writeString(KEY_OVERVIEW, on ? '1' : '0');
}

/**
 * Has this visitor ever pressed the button?
 *
 * The sparkle exists to answer "what is that lightning bolt for", and that
 * question is asked once. Someone who has used it — even to turn it straight
 * back off — has their answer, and animating at them after that is nagging
 * rather than teaching.
 */
export function loadOverviewSeen(): boolean {
  return readString(KEY_OVERVIEW_SEEN) === '1';
}

export function saveOverviewSeen(): void {
  writeString(KEY_OVERVIEW_SEEN, '1');
}

// ── The newest mode, asking to be tried ─────────────────────────────────────

const KEY_MODE_TRIED = 'gt_mode_tried';

/**
 * Has this visitor ever CHOSEN this mode?
 *
 * The same question the Overview sparkle answers, one control along. A mode
 * added after somebody formed their habits is invisible to them: the column
 * is six near-identical icons, and the eye stops reading it the moment it has
 * found the one it always uses. So the newest mode gets to wave — until it has
 * been tried once, after which waving is nagging (see the burst schedule in
 * ModeColumn).
 *
 * Stored per mode id rather than as one flag, because there will be a next new
 * mode and it should get its own introduction rather than inheriting this one's
 * "already seen".
 */
export function loadModeTried(id: string): boolean {
  return readString(`${KEY_MODE_TRIED}_${id}`) === '1';
}

export function saveModeTried(id: string): void {
  writeString(`${KEY_MODE_TRIED}_${id}`, '1');
}
